import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";

import Header from "../../../../Component/Common/Header";
import BlueButton from "../../../../Component/Common/BlueButton";

import Badge from "../../../../Component/Common/Badge";
import RegularLoader from "../../../../Component/Loader/RegularLoader";
import SectionCard from "../../../../Component/RetUserComponents/SectionCard";
import { Plus, RefreshCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import SimpleTable from "../../../../Component/Common/SimpleTable";
import RefreshButton from "../../../../Component/Common/RefreshButton";

export default function Retailers() {
  const navigate = useNavigate();

  const [list, setList] = useState([]);
  const [allRetailers, setAllRetailers] = useState([]);

  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeStates, setActiveStates] = useState({});

  const authUserString = localStorage.getItem("authUserData");
  const authUser = JSON.parse(authUserString);
  const loginUserID = authUser?.id;

  // FETCH PAGEWISE DATA
  const fetchRetailers = async () => {
    if (!loginUserID) return;

    setLoading(true);
    try {
      const res = await axios.get(
        `https://your-api.com/get-retailers?page=${page}&per_page=10&distributor_id=${loginUserID}`
      );

      const dataPage = res.data.data || {};

      setList(dataPage.data || []);
      setLastPage(dataPage.last_page || 1);

      const init = {};
      (dataPage.data || []).forEach((r) => {
        init[r.id] = r.is_active === 1;
      });
      setActiveStates(init);
    } catch (err) {
      console.error("Error fetching retailers:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRetailers();
  }, [page, loginUserID]);

  // FETCH ALL FOR SEARCH
  useEffect(() => {
    const fetchAllRetailers = async () => {
      if (!loginUserID) return;

      try {
        let all = [];
        let p = 1;
        let last = 1;

        while (p <= last) {
          const res = await axios.get(
            `https://your-api.com/get-retailers?page=${p}&per_page=10&distributor_id=${loginUserID}`
          );

          const pageData = res.data?.data;
          if (!pageData) break;

          all = [...all, ...(pageData.data || [])];
          last = pageData.last_page || 1;
          p++;
        }

        setAllRetailers(all);

        const init = {};
        all.forEach((r) => {
          init[r.id] = r.is_active === 1;
        });
        setActiveStates(init);
      } catch (err) {
        console.log("Error fetching all retailers:", err);
      }
    };

    fetchAllRetailers();
  }, [loginUserID]);

  // SEARCH FILTER
  const filtered = useMemo(() => {
    if (!filter) return list;

    const term = filter.toLowerCase();

    return allRetailers.filter(
      (r) =>
        (r.name && r.name.toLowerCase().includes(term)) ||
        (r.code && r.code.toLowerCase().includes(term)) ||
        (r.mobile && r.mobile.toLowerCase().includes(term))
    );
  }, [filter, list, allRetailers]);

  // TOGGLE ACTIVE
  const toggleRetailer = (retailer, newState) => {
    Swal.fire({
      title: newState ? "Activate?" : "Deactivate?",
      text: `Are you sure you want to update status for ${retailer.name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
    }).then((res) => {
      if (res.isConfirmed) updateRetailerStatus(retailer, newState);
    });
  };

  const updateRetailerStatus = async (retailer, newState) => {
    setActiveStates((prev) => ({
      ...prev,
      [retailer.id]: newState,
    }));

    try {
      await axios.post(
        `https://your-api.com/update-retailer/${retailer.id}`,
        { is_active: newState ? 1 : 0 }
      );

      Swal.fire(
        newState ? "Activated!" : "Deactivated!",
        `Retailer ${retailer.name} has been updated.`,
        "success"
      );
    } catch (err) {
      setActiveStates((prev) => ({
        ...prev,
        [retailer.id]: !newState,
      }));
      Swal.fire("Error", "Failed to update status.", "error");
    }
  };

  return (
    <div>
      <SectionCard>
        <div className="space-y-3 sm:space-y-4">
          <Header>
            <div className="flex flex-row sm:flex-row sm:items-center sm:gap-2 gap-2 w-full">
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Search by name, code, or mobile"
                className="w-full sm:w-1/2 rounded-lg border px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500"
              />

              <BlueButton onClick={() => navigate("/add-retailer")} className="flex items-center">
                <Plus size={14} className="mr-1" /> Add
              </BlueButton>

              <RefreshButton onClick={fetchRetailers} />
            </div>
          </Header>

          {loading ? (
            <div className="flex items-center justify-center w-full translate-y-10">
              <RegularLoader />
            </div>
          ) : (
            <>
              <SimpleTable
                headers={[
                  "Sr No.",
                  "Created At",
                  "Retailers Name (Code)",
                  "Mobile No",
                  "Address",
                  "District",
                  "Status",
                  "Actions",
                ]}
                rows={filtered.map((r, i) => [
                  (page - 1) * 10 + (i + 1), // Correct row number
                  new Date(r.created_at).toLocaleDateString(),
                  `${r.name} (${r.code})`,
                  r.mobile || "-",
                  r.address || "-",
                  r.district || "-",
                  <Badge
                    label={activeStates[r.id] ? "Active" : "Inactive"}
                    color={activeStates[r.id] ? "blue" : "red"}
                  />,
                  <BlueButton onClick={() => toggleRetailer(r, !activeStates[r.id])}>
                    {activeStates[r.id] ? "Deactivate" : "Activate"}
                  </BlueButton>,
                ])}
                currentPage={page}
                totalPages={lastPage}
                onPageChange={setPage}
              />
            </>
          )}
        </div>
      </SectionCard>
    </div>
  );
}



// import React, { useState, useMemo, useEffect } from "react";
// import axios from "axios";
// import Swal from "sweetalert2";

// import Header from "../../../../Component/Header";
// import BlueButton from "../../../../Component/BlueButton";
// import SimpleTable from "../../../../Component/SimpleTable";
// import Badge from "../../../../Component/Badge";
// import WarrantyLoader from "../../../../Component/Loader/RegularLoader";

// import { Plus, RefreshCcw } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import WarrantyCanvasLoader from "../../../../Component/Loader/RegularLoader";
// import RegularLoader from "../../../../Component/Loader/RegularLoader";
// import SectionCard from "../../../../Component/RetUserComponents/SectionCard";

// export default function Retailers() {
//   const navigate = useNavigate();
//   const [list, setList] = useState([]);
//   const [allRetailers, setAllRetailers] = useState([]);
//   const [currentPage, setCurrentPage] = useState(1);

//   const [page, setPage] = useState(1);
//   const [lastPage, setLastPage] = useState(1);

//   const [filter, setFilter] = useState("");
//   const [loading, setLoading] = useState(false);

//   const [activeStates, setActiveStates] = useState({});

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = JSON.parse(authUserString);
//   const loginUserID = authUser?.id;

//   // FETCH PAGEWISE DATA
//   const fetchRetailers = async () => {
//     if (!loginUserID) return;

//     setLoading(true);
//     try {
//       const res = await axios.get(
//         `https://your-api.com/get-retailers?page=${page}&per_page=10&distributor_id=${loginUserID}`
//       );

//       const dataPage = res.data.data || [];

//       setList(dataPage.data || []);
//       setLastPage(dataPage.last_page || 1);

//       const init = {};
//       (dataPage.data || []).forEach((r) => {
//         init[r.id] = r.is_active === 1;
//       });
//       setActiveStates(init);
//     } catch (err) {
//       console.error("Error fetching retailers:", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchRetailers();
//   }, [page, loginUserID]);

//   // FETCH ALL FOR SEARCH
//   useEffect(() => {
//     const fetchAllRetailers = async () => {
//       if (!loginUserID) return;

//       try {
//         let all = [];
//         let p = 1;
//         let last = 1;

//         while (p <= last) {
//           const res = await axios.get(
//             `https://your-api.com/get-retailers?page=${p}&per_page=10&distributor_id=${loginUserID}`
//           );

//           const pageData = res.data.data;
//           if (!pageData) break;

//           all = [...all, ...(pageData.data || [])];
//           last = pageData.last_page || 1;
//           p++;
//         }

//         setAllRetailers(all);

//         const init = {};
//         all.forEach((r) => {
//           init[r.id] = r.is_active === 1;
//         });
//         setActiveStates(init);
//       } catch (err) {
//         console.log("Error fetching all retailers:", err);
//       }
//     };

//     fetchAllRetailers();
//   }, [loginUserID]);

//   // SEARCH FILTER
//   const filtered = useMemo(() => {
//     if (!filter) return list;

//     const term = filter.toLowerCase();

//     return allRetailers.filter(
//       (r) =>
//         (r.name && r.name.toLowerCase().includes(term)) ||
//         (r.code && r.code.toLowerCase().includes(term)) ||
//         (r.mobile && r.mobile.toLowerCase().includes(term))
//     );
//   }, [filter, list, allRetailers]);

//   // TOGGLE ACTIVE
//   const toggleRetailer = (retailer, newState) => {
//     Swal.fire({
//       title: newState ? "Activate?" : "Deactivate?",
//       text: `Are you sure you want to update status for ${retailer.name}?`,
//       icon: "warning",
//       showCancelButton: true,
//       confirmButtonText: "Yes",
//       cancelButtonText: "Cancel",
//     }).then((res) => {
//       if (res.isConfirmed) updateRetailerStatus(retailer, newState);
//     });
//   };

//   const updateRetailerStatus = async (retailer, newState) => {
//     setActiveStates((prev) => ({
//       ...prev,
//       [retailer.id]: newState,
//     }));

//     try {
//       const res = await axios.post(
//         `https://your-api.com/update-retailer/${retailer.id}`,
//         { is_active: newState ? 1 : 0 }
//       );

//       Swal.fire(
//         newState ? "Activated!" : "Deactivated!",
//         `Retailer ${retailer.name} has been updated.`,
//         "success"
//       );
//     } catch (err) {
//       setActiveStates((prev) => ({
//         ...prev,
//         [retailer.id]: !newState,
//       }));
//       Swal.fire("Error", "Failed to update status.", "error");
//     }
//   };

//   return (
//     <div>
//       <SectionCard  >
//         <div className="space-y-3 sm:space-y-4">
//       <Header>
//         <div className="flex flex-row sm:flex-row sm:items-center sm:gap-2 gap-2 w-full">
//           <input
//             value={filter}
//             onChange={(e) => setFilter(e.target.value)}
//             placeholder="Search by name, code, or mobile"
//             className="w-full sm:w-1/2 rounded-lg border px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500"
//           />

//           <BlueButton
//             onClick={() => navigate("/add-retailer")}
//             className="flex items-center"
//           >
//             <Plus size={14} className="mr-1" /> Add
//           </BlueButton>

//           <BlueButton onClick={fetchRetailers} className="flex items-center">
//             <RefreshCcw size={14} />
//             <span className="hidden sm:inline ml-1">Refresh</span>
//           </BlueButton>
//         </div>
//       </Header>

//       {loading ? (
//         <div className="flex items-center justify-center w-full translate-y-10">
//           <RegularLoader />
//         </div>
//       ) : (
//         <>
//           <SimpleTable
//             headers={[
//               "Sr No.",
//               "Created At",
//               "Retailers Name (Code)",
//               "Mobile No",
//               "Address",
//               "District",
//               "Status",
//               "Consent",
//               "Bank Verified",
//               "GST Details",
//               "Actions",
//             ]}
//             rows={filtered.map((r, i) => [
//               <span key={r.id}>{i + 1}</span>,
//               <div key={`nm-${r.id}`} className="font-medium">
//                 {r.name}{" "}
//                 <span className="text-xs text-slate-500">({r.code})</span>
//               </div>,
//               <span key={`m-${r.id}`}>{r.mobile || "-"}</span>,
//               <span key={`d-${r.id}`}>{r.district || "-"}</span>,
//               <span key={`c-${r.id}`}>
//                 {new Date(r.created_at).toLocaleDateString()}
//               </span>,

//               <Badge
//                 key={`b-${r.id}`}
//                 label={activeStates[r.id] ? "Active" : "Inactive"}
//                 color={activeStates[r.id] ? "blue" : "red"}
//               />,

//               <div key={`btn-${r.id}`} className="flex gap-2">
//                 <BlueButton
//                   onClick={() =>
//                     toggleRetailer(r, !activeStates[r.id])
//                   }
//                 >
//                   {activeStates[r.id] ? "Deactivate" : "Activate"}
//                 </BlueButton>
//               </div>,
//             ])}
//           />

//           {!filter && totalPages > 1 && (
//               <Pagination
//                 currentPage={currentPage}
//                 totalPages={totalPages}
//                 onPageChange={setCurrentPage}
//               />
//             )}
//         </>
//       )}
//     </div>
//       </SectionCard>
//     </div>
//   );
// }
